import React, { useRef, useState } from "react";
import { Upload, Sparkles, Loader2, Check, X, Building2, Calendar, Hash, Package } from "lucide-react";
import { toast } from "sonner";
import StoreLayout from "../components/StoreLayout";
import { api } from "../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const absoluteImg = (url) => {
  if (!url) return "";
  if (url.startsWith("http") || url.startsWith("data:") || url.startsWith("blob:")) return url;
  if (url.startsWith("/api/")) return `${BACKEND_URL}${url}`;
  return url;
};

const TEE_STYLES = ["Oversized", "Regular Fit", "Slim Fit", "Polo", "Hoodie", "Crop Top"];
const COLORS = ["Black", "White", "Off-White", "Olive", "Charcoal", "Navy", "Custom"];
const PRINT_METHODS = ["Screen Print", "DTG (Digital)", "Embroidery", "Sublimation", "Heat Transfer"];
const BUDGETS = ["< ₹50K", "₹50K – ₹1L", "₹1L – ₹3L", "₹3L – ₹5L", "₹5L+"];
const SIZES = ["S", "M", "L", "XL", "XXL"];

const initial = {
  company_name: "",
  contact_name: "",
  email: "",
  phone: "",
  industry: "",
  tee_style: "Oversized",
  quantity: 50,
  color: "Black",
  size_breakdown: { S: 0, M: 0, L: 0, XL: 0, XXL: 0 },
  deadline: "",
  budget_range: "",
  print_method: "Screen Print",
  placement: "",
  notes: "",
};

function StatBlock({ icon: Icon, label, value }) {
  return (
    <div className="border border-[#27272A] p-5 bg-[#0A0A0A] flex items-start gap-3">
      <Icon size={22} className="text-[#CFF048] flex-shrink-0 mt-1" />
      <div>
        <p className="font-display font-black text-xl leading-tight">{value}</p>
        <p className="label-mono text-[#71717A] mt-1">{label}</p>
      </div>
    </div>
  );
}

export default function Bulk() {
  const [form, setForm] = useState(initial);
  const [designFile, setDesignFile] = useState(null);
  const [designPreview, setDesignPreview] = useState("");
  const [designUrl, setDesignUrl] = useState("");
  const [mockup, setMockup] = useState("");
  const [uploading, setUploading] = useState(false);
  const [mockupBusy, setMockupBusy] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef();

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setSize = (s, v) => setForm((f) => ({ ...f, size_breakdown: { ...f.size_breakdown, [s]: Math.max(0, parseInt(v || 0, 10) || 0) } }));
  const sizesSum = SIZES.reduce((s, k) => s + (form.size_breakdown[k] || 0), 0);

  const onPickDesign = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) {
      toast.error("File too large (max 10MB)");
      return;
    }
    setDesignFile(f);
    if (f.type.startsWith("image/")) {
      setDesignPreview(URL.createObjectURL(f));
    } else {
      setDesignPreview("");
    }
    setMockup("");
    // Upload immediately
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const r = await api.post("/bulk/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDesignUrl(r.data.url);
      toast.success("DESIGN UPLOADED");
    } catch (err) {
      toast.error("Upload failed", { description: err?.response?.data?.detail || "" });
      setDesignFile(null);
      setDesignPreview("");
    } finally {
      setUploading(false);
    }
  };

  const generateMockup = async () => {
    if (!designFile || !designFile.type.startsWith("image/")) {
      toast.error("Pick an image (PNG/JPG/WEBP) to generate a mockup");
      return;
    }
    setMockupBusy(true);
    try {
      // Read file as base64
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(designFile);
      });
      const r = await api.post("/bulk/mockup", {
        design_base64: b64,
        tshirt_color: (form.color || "Black").toLowerCase().includes("white") ? "white" : "black",
      });
      setMockup(r.data.image);
      toast.success("MOCKUP READY", { description: "Generated by AI — for preview only." });
    } catch (err) {
      toast.error("Mockup failed", { description: err?.response?.data?.detail || "" });
    } finally {
      setMockupBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (sizesSum > 0 && sizesSum !== form.quantity) {
      const ok = window.confirm(
        `Size breakdown totals ${sizesSum} but you entered quantity ${form.quantity}. Submit anyway?`
      );
      if (!ok) return;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast.error("Enter a valid email");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/bulk/inquiry", {
        ...form,
        design_url: designUrl,
        mockup_url: mockup || "",
      });
      setSubmitted(true);
      toast.success("REQUEST SENT", { description: "We'll reply within 24 hours." });
    } catch (err) {
      toast.error("Failed", { description: err?.response?.data?.detail || "" });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <StoreLayout>
        <section className="max-w-[800px] mx-auto px-4 sm:px-6 lg:px-10 py-24 text-center" data-testid="bulk-success">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-[#CFF048] text-black mb-6">
            <Check size={32} />
          </div>
          <p className="label-mono text-[#CFF048]">// REQUEST RECEIVED</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight mt-3 leading-[0.95]">
            We're on it.
          </h1>
          <p className="text-[#A1A1AA] mt-5 max-w-lg mx-auto">
            Your bulk inquiry is in our hands. Our team will review your spec and reply with a quote
            within <span className="text-[#CFF048]">24 business hours</span>. Check your inbox — you'll get a confirmation email shortly.
          </p>
          <div className="mt-8 flex justify-center gap-3 flex-wrap">
            <a href="/" className="btn-neon">BACK HOME</a>
            <button
              onClick={() => { setSubmitted(false); setForm(initial); setDesignFile(null); setDesignPreview(""); setDesignUrl(""); setMockup(""); }}
              className="border border-[#27272A] text-white font-mono px-6 py-3 hover:border-[#CFF048] hover:text-[#CFF048] transition-colors"
            >
              SUBMIT ANOTHER
            </button>
          </div>
        </section>
      </StoreLayout>
    );
  }

  return (
    <StoreLayout>
      {/* Hero */}
      <section className="border-b border-[#27272A] bg-gradient-to-b from-[#0A0A0A] to-black">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-16">
          <p className="label-mono text-[#CFF048]">// BULK & CORPORATE</p>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tight mt-3 leading-[0.95]">
            Dress your <span className="text-[#CFF048]">squad.</span>
          </h1>
          <p className="text-[#A1A1AA] mt-5 max-w-2xl">
            Bulk orders for brands, agencies, college fests, startups, and crews. Send us your design,
            see an instant AI mockup, get a custom quote in 24 hours.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-10" data-testid="bulk-stats">
            <StatBlock icon={Hash} label="MIN. ORDER" value="25 pcs" />
            <StatBlock icon={Package} label="CUSTOM PRINTS" value="Screen · DTG · Embroidery" />
            <StatBlock icon={Calendar} label="TURNAROUND" value="7 – 14 days" />
            <StatBlock icon={Building2} label="GST INVOICE" value="Always included" />
          </div>
        </div>
      </section>

      {/* Form */}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-12">
        <form onSubmit={submit} className="grid lg:grid-cols-[1.3fr_1fr] gap-8" data-testid="bulk-form">
          {/* LEFT — fields */}
          <div className="space-y-6">
            {/* Company info */}
            <div className="border border-[#27272A] p-6 bg-[#0A0A0A]">
              <p className="label-mono text-[#CFF048] mb-4">// 01 · YOU</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-mono text-[#71717A] mb-2 block">COMPANY / BRAND *</label>
                  <input className="cult-input" required value={form.company_name} onChange={(e) => setField("company_name", e.target.value)} placeholder="e.g. Acme Streetwear" data-testid="bulk-company" />
                </div>
                <div>
                  <label className="label-mono text-[#71717A] mb-2 block">CONTACT PERSON *</label>
                  <input className="cult-input" required value={form.contact_name} onChange={(e) => setField("contact_name", e.target.value)} placeholder="Your name" data-testid="bulk-contact" />
                </div>
                <div>
                  <label className="label-mono text-[#71717A] mb-2 block">EMAIL *</label>
                  <input type="email" className="cult-input" required value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="you@company.com" data-testid="bulk-email" />
                </div>
                <div>
                  <label className="label-mono text-[#71717A] mb-2 block">PHONE</label>
                  <input className="cult-input" value={form.phone} onChange={(e) => setField("phone", e.target.value)} placeholder="+91 ..." data-testid="bulk-phone" />
                </div>
                <div className="sm:col-span-2">
                  <label className="label-mono text-[#71717A] mb-2 block">INDUSTRY / USE CASE</label>
                  <input className="cult-input" value={form.industry} onChange={(e) => setField("industry", e.target.value)} placeholder="Fashion brand · Startup swag · College fest · Event ..." data-testid="bulk-industry" />
                </div>
              </div>
            </div>

            {/* Spec */}
            <div className="border border-[#27272A] p-6 bg-[#0A0A0A]">
              <p className="label-mono text-[#CFF048] mb-4">// 02 · THE SPEC</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="label-mono text-[#71717A] mb-2 block">TEE STYLE</label>
                  <select className="cult-input" value={form.tee_style} onChange={(e) => setField("tee_style", e.target.value)} data-testid="bulk-style">
                    {TEE_STYLES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono text-[#71717A] mb-2 block">PRIMARY COLOR</label>
                  <select className="cult-input" value={form.color} onChange={(e) => setField("color", e.target.value)} data-testid="bulk-color">
                    {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono text-[#71717A] mb-2 block">QUANTITY * (MIN 25)</label>
                  <input type="number" min="25" max="100000" className="cult-input" required value={form.quantity} onChange={(e) => setField("quantity", parseInt(e.target.value || 0, 10))} data-testid="bulk-quantity" />
                </div>
                <div>
                  <label className="label-mono text-[#71717A] mb-2 block">DEADLINE</label>
                  <input type="date" className="cult-input" value={form.deadline} onChange={(e) => setField("deadline", e.target.value)} data-testid="bulk-deadline" />
                </div>
                <div>
                  <label className="label-mono text-[#71717A] mb-2 block">PRINT METHOD</label>
                  <select className="cult-input" value={form.print_method} onChange={(e) => setField("print_method", e.target.value)} data-testid="bulk-print">
                    {PRINT_METHODS.map((p) => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-mono text-[#71717A] mb-2 block">BUDGET RANGE</label>
                  <select className="cult-input" value={form.budget_range} onChange={(e) => setField("budget_range", e.target.value)} data-testid="bulk-budget">
                    <option value="">— Optional —</option>
                    {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="label-mono text-[#71717A] mb-2 block">DESIGN PLACEMENT</label>
                  <input className="cult-input" value={form.placement} onChange={(e) => setField("placement", e.target.value)} placeholder="Front chest · Back · Sleeve · Neck tag ..." data-testid="bulk-placement" />
                </div>
              </div>

              {/* Size breakdown */}
              <div className="mt-5">
                <label className="label-mono text-[#71717A] mb-2 block">SIZE BREAKDOWN <span className="text-[#52525B]">(optional · sum should match quantity)</span></label>
                <div className="grid grid-cols-5 gap-2">
                  {SIZES.map((s) => (
                    <div key={s}>
                      <p className="label-mono text-[#CFF048] mb-1 text-center">{s}</p>
                      <input
                        type="number"
                        min="0"
                        value={form.size_breakdown[s]}
                        onChange={(e) => setSize(s, e.target.value)}
                        className="cult-input text-center"
                        data-testid={`bulk-size-${s}`}
                      />
                    </div>
                  ))}
                </div>
                <p className="label-mono text-[#71717A] mt-2 text-right">
                  TOTAL: <span className={sizesSum === form.quantity ? "text-[#CFF048]" : "text-yellow-400"}>{sizesSum}</span> / {form.quantity}
                </p>
              </div>
            </div>

            {/* Notes */}
            <div className="border border-[#27272A] p-6 bg-[#0A0A0A]">
              <p className="label-mono text-[#CFF048] mb-4">// 03 · ANYTHING ELSE</p>
              <textarea
                className="cult-input"
                rows={4}
                placeholder="GST invoice? Custom neck tag? Special packaging? Drop the details here."
                value={form.notes}
                onChange={(e) => setField("notes", e.target.value.slice(0, 1500))}
                data-testid="bulk-notes"
              />
              <p className="label-mono text-[#71717A] text-right text-[10px] mt-1">{form.notes.length} / 1500</p>
            </div>
          </div>

          {/* RIGHT — design + mockup + submit */}
          <div className="space-y-6 lg:sticky lg:top-24 self-start">
            <div className="border border-[#27272A] p-6 bg-[#0A0A0A]">
              <p className="label-mono text-[#CFF048] mb-4">// DESIGN UPLOAD</p>
              <input
                ref={fileRef}
                type="file"
                accept=".jpg,.jpeg,.png,.webp,.svg,.pdf,.ai,.psd"
                onChange={onPickDesign}
                className="hidden"
                data-testid="bulk-design-input"
              />
              {!designFile ? (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full border-2 border-dashed border-[#27272A] hover:border-[#CFF048] py-10 flex flex-col items-center gap-2 text-[#71717A] hover:text-[#CFF048] transition-colors"
                  data-testid="bulk-pick-design"
                >
                  <Upload size={26} />
                  <span className="label-mono">UPLOAD LOGO / ARTWORK</span>
                  <span className="text-[10px] text-[#52525B]">JPG · PNG · WEBP · SVG · PDF · AI · PSD · MAX 10MB</span>
                </button>
              ) : (
                <div className="space-y-3">
                  {designPreview ? (
                    <img src={designPreview} alt="" className="w-full max-h-[260px] object-contain bg-black border border-[#27272A]" />
                  ) : (
                    <div className="bg-black border border-[#27272A] p-6 text-center">
                      <p className="label-mono text-[#CFF048]">// {designFile.name.split('.').pop().toUpperCase()} FILE</p>
                      <p className="font-display font-bold mt-1 truncate">{designFile.name}</p>
                      <p className="label-mono text-[#71717A] text-[10px] mt-1">{(designFile.size / 1024).toFixed(1)} KB</p>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <p className="label-mono text-[#71717A] truncate flex-1">// {designFile.name}</p>
                    <button
                      type="button"
                      onClick={() => { setDesignFile(null); setDesignPreview(""); setDesignUrl(""); setMockup(""); }}
                      className="text-[#71717A] hover:text-red-400 ml-2"
                      data-testid="bulk-remove-design"
                    >
                      <X size={16} />
                    </button>
                  </div>
                  {uploading && (
                    <p className="label-mono text-[#71717A] flex items-center gap-2">
                      <Loader2 size={12} className="animate-spin" /> UPLOADING...
                    </p>
                  )}
                  {designUrl && (
                    <button
                      type="button"
                      onClick={generateMockup}
                      disabled={mockupBusy || !designFile.type.startsWith("image/")}
                      className="w-full border border-[#CFF048] text-[#CFF048] font-mono font-bold py-3 flex items-center justify-center gap-2 hover:bg-[#CFF048] hover:text-black transition-colors disabled:opacity-50"
                      data-testid="bulk-generate-mockup"
                    >
                      {mockupBusy ? <Loader2 size={16} className="animate-spin" /> : <Sparkles size={16} />}
                      {mockupBusy ? "GENERATING MOCKUP..." : "✨ PREVIEW ON TEE (AI)"}
                    </button>
                  )}
                </div>
              )}
            </div>

            {mockup && (
              <div className="border border-[#CFF048] p-3 bg-[#0A0A0A]" data-testid="bulk-mockup-preview">
                <p className="label-mono text-[#CFF048] mb-2 px-2 pt-1">// AI MOCKUP PREVIEW</p>
                <img src={absoluteImg(mockup)} alt="Mockup" className="w-full bg-black" />
                <p className="label-mono text-[#71717A] mt-2 px-2 pb-1 text-[10px]">
                  // FOR PREVIEW ONLY. FINAL OUTPUT MAY VARY BASED ON FABRIC + INK.
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="btn-neon w-full flex items-center justify-center gap-2 disabled:opacity-50 text-base py-4"
              data-testid="bulk-submit"
            >
              {submitting ? <Loader2 size={18} className="animate-spin" /> : null}
              {submitting ? "SENDING..." : "REQUEST A QUOTE →"}
            </button>
            <p className="label-mono text-[#71717A] text-[10px] text-center">
              // WE REPLY WITHIN 24 BUSINESS HOURS. NO SPAM. PROMISE.
            </p>
          </div>
        </form>
      </section>
    </StoreLayout>
  );
}
